// src/ecrans/calendrier/EcranCalendrier.jsx — LE CALENDRIER NEST (maquette
// v677, « Admin › Calendrier », relevés 222 à 237).
//
// Lu SEULEMENT par les portes du serveur : ecran_logements_lire,
// ecran_calendrier_sejours_lire { du, au }, ecran_fermetures_lire.
// Trois vues : Tableau (ordinateur), Jour (téléphone, par défaut), Liste.
//
// PAS ICI (listé dans la demande) : les prix par nuit et les règles
// (séjour minimum) — lecture de prix, couloir argent ; « Bloquer une
// période » — une écriture, hors de ce lot de lecture ; la fiche d'une
// réservation — elle attend le socle.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLecture } from "../portes";
import {
  StyleNest,
  EnTete,
  Tuile,
  EtatLecture,
  LienAncien,
  Ic,
  ICO,
  C,
  PLATEFORMES,
  plateformeDe,
  aujourdhuiParis,
  decalerIso,
  jourEnLettres,
  jourCourt,
  dateIso,
  MOIS,
  mot,
  pl,
} from "../nest";
import { heureH as heureCourte } from "../accueil/accueil-calculs";
import { chiffreSuivi, couvertureDe, idsNonSuivis, nonCouverts } from "../couverture";
import useEstEtroit from "../useEstEtroit";
import {
  phraseExclusCalendrier,
  bornesMois,
  moisDecale,
  joursEntre,
  chiffresMois,
  arriveesEntre,
  filtrer,
  placeBarre,
  vueJour,
  nuits,
  estAnnule,
  estFerme,
} from "./calendrier-calculs";

const CSS = `
.ncal-cmd{background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:11px 12px;margin-bottom:12px}
.ncal-r{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:8px}
.ncal-mois{display:flex;align-items:center;gap:4px;background:#E3F1EB;border-radius:11px;padding:3px}
.ncal-mois b{font-size:12.5px;font-weight:700;padding:0 4px;min-width:84px;text-align:center}
.ncal-fl{all:unset;cursor:pointer;width:38px;height:38px;border-radius:8px;background:#fff;display:flex;align-items:center;justify-content:center}
.ncal-g{background:#fff;border:1px solid ${C.bord};border-radius:14px;overflow-x:auto}
.ncal-grille{display:grid;position:relative;width:max-content}
.ncal-coin{position:sticky;left:0;z-index:3;background:#fff;display:flex;align-items:center;padding:0 8px 0 12px;font-size:10.5px;font-weight:600;color:${C.gris};border-bottom:1px solid ${C.bord};border-right:1px solid ${C.bord};min-height:48px}
.ncal-hd{display:flex;flex-direction:column;align-items:center;justify-content:center;border-bottom:1px solid ${C.bord};min-height:48px}
.ncal-hd b{font-size:13px;font-weight:600}
.ncal-hd small{font-size:10.5px;color:${C.grisD}}
.ncal-hd.we,.ncal-c.we{background:#F8F9F8}
.ncal-hd.auj{background:#FDF0EB;border-bottom:2px solid ${C.aub}}
.ncal-hd.auj b{color:${C.aubTx}}
.ncal-nom{position:sticky;left:0;z-index:2;background:#fff;padding:6px 8px 6px 12px;border-bottom:1px solid ${C.bord};border-right:1px solid ${C.bord};min-height:52px;display:flex;flex-direction:column;justify-content:center}
.ncal-nom b{font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ncal-nom small{font-size:10.5px;color:#5E6E68}
.ncal-c{border-bottom:1px solid ${C.bord};border-left:1px solid #F0F2F1;min-height:52px}
.ncal-c.auj{background:#FDF0EB}
.ncal-bar{position:absolute;height:40px;border-radius:7px;padding:0 6px;display:flex;align-items:center;gap:3px;font-size:10.5px;font-weight:600;overflow:hidden;white-space:nowrap;box-sizing:border-box}
.ncal-bar.ann{text-decoration:line-through;opacity:.6}
.ncal-pied{font-size:11px;color:${C.gris};text-align:right;margin:8px 2px 0}
.ncal-jl{all:unset;box-sizing:border-box;display:flex;align-items:center;gap:10px;width:100%;background:#fff;border:1px solid ${C.bord};border-radius:12px;padding:10px 12px;margin-bottom:6px;min-height:52px}
.ncal-jl .pc{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:13px}
.ncal-jl .t{display:block;font-size:12.5px;font-weight:600}
.ncal-jl .s{display:block;font-size:11px;color:${C.gris};margin-top:2px}
.ncal-pf{display:inline-flex;align-items:center;gap:5px;font-size:12px}
.ncal-pf i{width:7px;height:7px;border-radius:50%;display:inline-block}
@media (max-width:640px){
 .ncal-cache-tel{display:none}
 .ncal-nom{padding:5px 6px 5px 8px;min-height:56px}
 .ncal-nom b{white-space:normal;line-height:1.15;font-size:11.5px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
 .ncal-nom small{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:9.5px}
 .ncal-c{min-height:56px}
}
`;

// Les mesures de la grille, comme la maquette (`dims`).
const GRILLE = { ordi: { col: 42, nom: 244, ligne: 52 }, tel: { col: 44, nom: 132, ligne: 56 } };
const HAUT_ENTETE = 48;
const JOURS_ABR = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];

function couleurs(s) {
  const pf = plateformeDe(s.source, s.type);
  return PLATEFORMES[pf] || PLATEFORMES.ferme;
}

function Barre({ s, place, ligne, g }) {
  const k = couleurs(s);
  const ferme = estFerme(s);
  return (
    <span
      className={`ncal-bar${estAnnule(s) ? " ann" : ""}`}
      title={`${ferme ? "Blocage" : s.voyageur || "Voyageur"} · ${jourCourt(s.arrivee)} → ${jourCourt(s.depart)}${estAnnule(s) ? " · annulée" : ""}`}
      style={{
        left: g.nom + place.gauche * g.col + 1,
        width: place.largeur * g.col - 2,
        top: HAUT_ENTETE + ligne * g.ligne + (g.ligne - 40) / 2,
        background: k.fond,
        border: `1px solid ${k.bord}`,
        color: k.tx,
      }}
    >
      {ferme ? <Ic d={ICO.cadenas} t={12} titre="Blocage" /> : null}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{ferme ? "Blocage" : s.voyageur || "Voyageur"}</span>
    </span>
  );
}

function Tableau({ logements, sejours, jours, today, exclus, g }) {
  const lignes = logements.map((l, i) => ({ l, i }));
  const index = new Map(logements.map((l, i) => [l.id, i]));
  // Comme la maquette : aujourd'hui visible, une semaine avant lui à gauche.
  const boite = useRef(null);
  useEffect(() => {
    const i = jours.indexOf(today);
    const el = boite.current;
    if (!el || i <= 7) return;
    // Aligné sur une colonne entière, même au bout du mois (sinon la première
    // colonne visible est coupée sous les noms).
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = Math.floor(Math.min((i - 7) * g.col, max) / g.col) * g.col;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jours[0], today, g]);
  return (
    <div className="ncal-g" data-testid="calendrier-tableau" ref={boite}>
      <div
        className="ncal-grille"
        style={{ gridTemplateColumns: `${g.nom}px repeat(${jours.length}, ${g.col}px)` }}
      >
        <div className="ncal-coin">
          <span>
            Logement
            <br />
            <small style={{ fontWeight: 400 }}>vue commune</small>
          </span>
        </div>
        {jours.map((d) => {
          const w = dateIso(d).getUTCDay();
          const cls = ["ncal-hd", w === 0 || w === 6 ? "we" : "", d === today ? "auj" : ""].join(" ");
          return (
            <div key={d} className={cls}>
              <b>{Number(d.slice(8))}</b>
              <small>{JOURS_ABR[w]}</small>
            </div>
          );
        })}
        {lignes.map(({ l }) => [
          <div key={`n-${l.id}`} className="ncal-nom">
            <b title={l.nom}>{l.nom}</b>
            {exclus && exclus.has(l.id) ? <small style={{ color: C.aub }}>sans source vivante</small> : l.parent ? <small>chambre · {l.parent.nom}</small> : null}
          </div>,
          ...jours.map((d) => {
            const w = dateIso(d).getUTCDay();
            return (
              <div
                key={`${l.id}-${d}`}
                className={["ncal-c", w === 0 || w === 6 ? "we" : "", d === today ? "auj" : ""].join(" ")}
              />
            );
          }),
        ])}
        {sejours.map((s) => {
          const ligne = s.logement ? index.get(s.logement.id) : undefined;
          const place = placeBarre(s, jours);
          if (ligne === undefined || !place) return null;
          return <Barre key={`${s.type}-${s.id}`} s={s} place={place} ligne={ligne} g={g} />;
        })}
      </div>
    </div>
  );
}

function LigneJour({ ic, fond, couleur, titre, sous, droite }) {
  return (
    <div className="ncal-jl">
      <span className="pc" style={{ background: fond, color: couleur }} aria-hidden="true">
        {ic}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="t">{titre}</span>
        <span className="s">{sous}</span>
      </span>
      {droite}
    </div>
  );
}

const pfNom = (s) => (PLATEFORMES[plateformeDe(s.source, s.type)] || {}).nom || "";
const nomL = (s) => (s.logement ? s.logement.nom : "Logement inconnu");

/** « 23 août » → « sam. 29 août » (la maquette met le jour de la semaine). */
function jourAbr(iso) {
  return `${JOURS_ABR[dateIso(iso).getUTCDay()]} ${jourCourt(iso)}`;
}

/** La pastille d'heure : l'heure annoncée si on la sait, sinon « au contrat ». */
function PastilleHeure({ h }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, padding: "5px 9px", borderRadius: 9, background: "#F4F6F5", whiteSpace: "nowrap", flexShrink: 0 }}>
      {h ? <b style={{ fontSize: 14, fontWeight: 600 }}>{h}</b> : null}
      <span style={{ fontSize: 9.5, color: C.gris }}>{h ? "annoncée" : "au contrat"}</span>
    </span>
  );
}

function VueJour({ lecture, jour, setJour, today, heures, onAller }) {
  const [filtre, setFiltre] = useState("tout");
  if (lecture.etat !== "ok") return null;
  const v = vueJour(lecture.data.sejours || [], jour);
  const entrees = [
    ["tout", `Tout · ${v.total}`],
    ["rotations", `Rotations · ${v.rotations.length}`],
    ["departs", `Départs · ${v.departs.length}`],
    ["arrivees", `Arrivées · ${v.arrivees.length}`],
    ["surplace", `Sur place · ${v.surPlace.length}`],
  ];
  const voir = (k) => filtre === "tout" || filtre === k;
  return (
    <div data-testid="calendrier-jour">
      <div className="nest-carte" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <button type="button" className="nest-bt" aria-label="Jour précédent" onClick={() => setJour(decalerIso(jour, -1))}>←</button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <b style={{ fontSize: 15 }}>{jourEnLettres(jour)} {jour.slice(0, 4)}</b>
          {jour === today ? (
            <div><span style={{ fontSize: 10.5, background: C.vertP, color: C.vertF, borderRadius: 9, padding: "1px 8px" }}>aujourd'hui</span></div>
          ) : null}
        </div>
        <button type="button" className="nest-bt" aria-label="Jour suivant" onClick={() => setJour(decalerIso(jour, 1))}>→</button>
      </div>
      <div className="nest-seg" style={{ marginBottom: 10 }}>
        {entrees.map(([k, t]) => (
          <button key={k} type="button" className={filtre === k ? "on" : ""} onClick={() => setFiltre(k)}>{t}</button>
        ))}
      </div>
      {voir("rotations") && v.rotations.map((r) => (
        <LigneJour key={`r-${r.depart.id}`} ic="⇄" fond="#FAE5E5" couleur="#C94A4A"
          titre={`Rotation · ${nomL(r.depart)}`}
          sous={`${r.depart.voyageur || "Voyageur"} part, ${r.arrivee.voyageur || "voyageur"} arrive`} />
      ))}
      {voir("departs") && v.departs.map((s) => (
        <LigneJour key={`d-${s.id}`} ic="↓" fond={C.aubP} couleur={C.aub}
          titre={`Départ · ${nomL(s)}`} sous={[s.voyageur, pfNom(s), pl(nuits(s), "nuit")].filter(Boolean).join(" · ")}
          droite={<PastilleHeure h={heures && heures.get(`d:${s.id}`)} />} />
      ))}
      {voir("arrivees") && v.arrivees.map((s) => (
        <LigneJour key={`a-${s.id}`} ic="↑" fond={C.vertP} couleur={C.vertF}
          titre={`Arrivée · ${nomL(s)}`} sous={[s.voyageur, pfNom(s), pl(nuits(s), "nuit")].filter(Boolean).join(" · ")}
          droite={<PastilleHeure h={heures && heures.get(`a:${s.id}`)} />} />
      ))}
      {v.total === 0 && filtre !== "surplace" ? <p className="nest-etat">Ni arrivée ni départ ce jour-là.</p> : null}
      {voir("surplace") ? (
        <>
          <p className="nest-inter" style={{ color: C.vertF }}>Sur place ce soir · {v.surPlace.length}</p>
          {v.surPlace.map((s) => (
            <LigneJour key={`p-${s.id}`} ic={s.arrivee === jour ? "↑" : "●"} fond={s.arrivee === jour ? C.vertP : "#F4F6F5"} couleur={s.arrivee === jour ? C.vertF : C.gris}
              titre={s.voyageur || "Voyageur"}
              sous={[nomL(s), pfNom(s), s.arrivee === jour ? (jour === today ? "arrive aujourd'hui" : `arrive le ${jourAbr(jour)}`) : null, s.depart === decalerIso(jour, 1) ? `part demain, ${jourAbr(s.depart)}` : `part le ${jourAbr(s.depart)}`].filter(Boolean).join(" · ")} />
          ))}
        </>
      ) : null}
      {filtre === "tout" ? <Demain sejours={lecture.data.sejours || []} jour={decalerIso(jour, 1)} onVoir={jour === today && onAller ? () => onAller("menages") : null} /> : null}
    </div>
  );
}

/** « Demain, lundi 24 août » : départs, arrivées et blocages du lendemain. */
function Demain({ sejours, jour, onVoir }) {
  const v = vueJour(sejours, jour);
  const blocs = sejours.filter((s) => estFerme(s) && s.arrivee === jour);
  const departs = [...v.rotations.map((r) => r.depart), ...v.departs];
  const arrivees = [...v.rotations.map((r) => r.arrivee), ...v.arrivees];
  return (
    <div data-testid="calendrier-demain">
      <p className="nest-inter" style={{ color: C.vertF }}>Demain, {jourEnLettres(jour).toLowerCase()}</p>
      {!departs.length && !arrivees.length && !blocs.length ? <p className="nest-etat">Ni arrivée, ni départ, ni blocage.</p> : null}
      {departs.map((s) => (
        <LigneJour key={`dd-${s.id}`} ic="↓" fond={C.aubP} couleur={C.aub} titre={`Départ · ${nomL(s)}`} sous={[s.voyageur, pfNom(s)].filter(Boolean).join(" · ")} />
      ))}
      {arrivees.map((s) => (
        <LigneJour key={`da-${s.id}`} ic="↑" fond={C.vertP} couleur={C.vertF} titre={`Arrivée · ${nomL(s)}`} sous={[s.voyageur, pfNom(s), pl(nuits(s), "nuit")].filter(Boolean).join(" · ")} />
      ))}
      {blocs.map((s) => (
        <LigneJour key={`db-${s.id}`} ic={<Ic d={ICO.cadenas} t={13} titre="Blocage" />} fond="#F4F6F5" couleur={C.gris} titre={`Blocage · ${nomL(s)}`} sous={`jusqu'au ${jourCourt(s.depart)}`} />
      ))}
      {onVoir ? <button type="button" className="nest-bt" style={{ width: "100%", justifyContent: "center", marginTop: 4 }} onClick={onVoir}>Voir les ménages de demain ›</button> : null}
    </div>
  );
}

function Liste({ sejours }) {
  if (!sejours.length) return <p className="nest-etat">Aucun séjour sur cette période.</p>;
  return (
    <div data-testid="calendrier-liste">
      {sejours.map((s) => {
        const k = couleurs(s);
        return (
          <LigneJour key={`${s.type}-${s.id}`} ic={estFerme(s) ? <Ic d={ICO.cadenas} t={13} titre="Blocage" /> : "●"} fond={k.fond} couleur={k.tx}
            titre={`${estFerme(s) ? "Blocage" : s.voyageur || "Voyageur"} · ${nomL(s)}`}
            sous={[`${jourCourt(s.arrivee)} → ${jourCourt(s.depart)}`, pl(nuits(s), "nuit"), pfNom(s), estAnnule(s) ? "annulée" : null].filter(Boolean).join(" · ")} />
        );
      })}
    </div>
  );
}

export default function EcranCalendrier({ onAller, onAncien }) {
  const today = useMemo(() => aujourdhuiParis(), []);
  const etroit = useEstEtroit();
  const [vue, setVue] = useState(etroit ? "jour" : "tableau");
  const [mois, setMois] = useState(today.slice(0, 7));
  const [jour, setJour] = useState(today);
  const [f, setF] = useState({ plateforme: "toutes", texte: "", logement: "", statut: "tous", annulees: true });
  const [voirFermetures, setVoirFermetures] = useState(false);
  const { du, au } = bornesMois(mois);
  const [logements, relireLogements] = useLecture("ecran_logements_lire", {});
  const [sejours, relireSejours] = useLecture("ecran_calendrier_sejours_lire", { du, au });
  const [semaine] = useLecture("ecran_calendrier_sejours_lire", { du: today, au: decalerIso(today, 7) });
  const [autourDuJour] = useLecture("ecran_calendrier_sejours_lire", { du: decalerIso(jour, -1), au: decalerIso(jour, 2) });
  const [fermetures, relireFermetures] = useLecture("ecran_fermetures_lire", {});
  // La couverture des sources vient de la porte de la journée (celle du
  // calendrier ne la rend pas) : sans elle, remplissage et nuits libres « — ».
  const [journee] = useLecture("ecran_journee_lire", { jours: 1 });
  const couverture = journee.etat === "ok" ? nonCouverts(journee.data) : null;
  const couvertureSure = Boolean(couverture && !couverture.inconnu);
  // Les heures annoncées du jour (porte de la journée) : « 16h annoncée ».
  const heuresDuJour = new Map();
  if (journee.etat === "ok" && journee.data.jours && journee.data.jours[0]) {
    const j0 = journee.data.jours[0];
    for (const a of j0.arrivees || []) if (a.heure) heuresDuJour.set(`a:${a.id}`, heureCourte(a.heure));
    for (const d of j0.departs || []) if (d.heure) heuresDuJour.set(`d:${d.id}`, heureCourte(d.heure));
  }
  const exclus = idsNonSuivis(couvertureDe(journee));

  const logs = logements.etat === "ok" ? logements.data.logements || [] : null;
  const tous = sejours.etat === "ok" ? sejours.data.sejours || [] : null;
  const chiffres = chiffresMois({ logements: logs, sejours: tous, du, au, exclus });
  // Le pied compte TOUS les séjours lus, couverts ou non.
  const pied = chiffresMois({ logements: logs, sejours: tous, du, au });
  const phraseExclus = couvertureSure && logs ? phraseExclusCalendrier(couverture.logements, logs) : null;
  const nomMois = MOIS[Number(mois.slice(5)) - 1];
  const nbOuDash = (lect, v) => (lect.some((l) => l.etat === "lecture") ? "…" : lect.some((l) => l.etat !== "ok") || v === null || v === undefined ? "—" : v);
  const nbFerm = fermetures.etat === "ok" ? fermetures.data.nb : null;
  // LA RÈGLE DES CHIFFRES (src/ecrans/couverture.js) : remplissage, nuits
  // libres et arrivées de la semaine ne comptent que les logements suivis ;
  // une partie suivie → « sur N logements suivis » ; aucun → « — ».
  const cv = couvertureDe(journee);
  const suivi = (lect, n) =>
    lect.some((l) => l.etat === "lecture") ? chiffreSuivi("…", cv) : lect.some((l) => l.etat !== "ok") ? chiffreSuivi(NaN, cv) : chiffreSuivi(n ?? NaN, cv);
  const rempli = suivi([logements, sejours], chiffres ? chiffres.rempli : null);
  const libres = suivi([logements, sejours], chiffres ? chiffres.libres : null);
  const semaineN = suivi([semaine], semaine.etat === "ok" ? arriveesEntre(semaine.data.sejours, today, decalerIso(today, 7), exclus).length : null);
  const jours = joursEntre(du, au);
  const visibles = tous ? filtrer(tous, f) : [];
  const logsVisibles = logs ? logs.filter((l) => !f.logement || l.id === f.logement) : [];

  return (
    <div className="nest" data-testid="ecran-calendrier-nest">
      <StyleNest />
      <style>{CSS}</style>
      <EnTete sous="Réservations, blocages, prix et règles" titre="Calendrier">
        <Tuile n={typeof rempli.valeur === "number" ? `${rempli.valeur} %` : rempli.valeur} detail={rempli.sur} libelle={`rempli en ${nomMois} ›`} testId="cal-tuile-rempli" onClick={() => setVue("tableau")} />
        <Tuile n={libres.valeur} detail={libres.sur} libelle={`${mot(libres.valeur, "nuit libre", "nuits libres")} en ${nomMois} ›`} testId="cal-tuile-libres" onClick={() => setVue("tableau")} />
        <Tuile n={nbOuDash([fermetures], nbFerm)} alerte={nbFerm > 0} ouverte={voirFermetures}
          libelle={`${mot(nbFerm, "fermeture non confirmée", "fermetures non confirmées")} ›`} testId="cal-tuile-fermetures"
          onClick={() => setVoirFermetures((v) => !v)} />
        <Tuile n={semaineN.valeur} detail={semaineN.sur} libelle={`${mot(semaineN.valeur, "arrivée")} cette semaine ›`} testId="cal-tuile-semaine" onClick={() => { setJour(today); setVue("jour") }} />
      </EnTete>

      <div className="nest-page">
        <EtatLecture lecture={logements} quoi="Les logements" onRelire={relireLogements} />
        {logements.etat === "ok" && logements.data.source === "configuration" ? (
          <p className="nest-manque">— {logements.data.repli_motif || "Liste des logements lue dans la configuration."}</p>
        ) : null}
        {phraseExclus ? <p className="nest-manque" data-testid="cal-exclus">{phraseExclus}</p> : null}
        {journee.etat === "echec" || (couverture && couverture.inconnu) ? (
          <p className="nest-manque" data-testid="cal-couverture-inconnue">— Couverture des sources de réservations inconnue : remplissage, nuits libres et arrivées de la semaine ne sont pas calculés.</p>
        ) : null}
        {voirFermetures ? (
          <div className="nest-carte" style={{ marginBottom: 12 }} data-testid="cal-fermetures">
            <EtatLecture lecture={fermetures} quoi="Les fermetures" onRelire={relireFermetures} />
            {fermetures.etat === "ok" && !fermetures.data.fermetures.length ? <p className="nest-etat">Toutes les fermetures sont confirmées.</p> : null}
            {fermetures.etat === "ok" && fermetures.data.fermetures.map((x) => (
              <LigneJour key={x.id} ic="!" fond={C.aubP} couleur={C.aub}
                titre={`${x.geste === "rouvrir" ? "Réouverture" : "Fermeture"} · ${x.logement ? x.logement.nom : "Logement inconnu"}`}
                sous={[`${jourCourt(x.du)} → ${jourCourt(x.au)}`, x.plateforme, x.etat === "echec" ? `refusée ou sans réponse · ${pl(x.essais, "essai")}` : "en cours d'envoi"].filter(Boolean).join(" · ")} />
            ))}
          </div>
        ) : null}

        {etroit ? (
          <>
            <span className="nest-seg" style={{ marginBottom: 10 }}>
              {[["jour", "Jour"], ["tableau", "Tableau"], ["liste", "Liste"]].map(([k, t]) => (
                <button key={k} type="button" className={vue === k ? "on" : ""} onClick={() => setVue(k)}>{t}</button>
              ))}
            </span>
            <div className="ncal-cmd" data-testid="cal-commandes-tel">
              <input className="nest-champ" style={{ width: "100%", marginBottom: 8 }} placeholder="Voyageur ou logement…" value={f.texte} onChange={(e) => setF((x) => ({ ...x, texte: e.target.value }))} />
              <div style={{ display: "flex", gap: 8 }}>
                <select className="nest-sel" style={{ flex: 1, minWidth: 0 }} value={f.logement} onChange={(e) => setF((x) => ({ ...x, logement: e.target.value }))} aria-label="Logement">
                  <option value="">Tous les logements</option>
                  {(logs || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
                </select>
                <button type="button" className="nest-bt p" style={{ flex: 1, justifyContent: "center" }} disabled title="Une écriture : elle n'est pas dans ce lot de lecture.">Bloquer</button>
              </div>
              <div style={{ marginTop: 8 }}>
                <LienAncien pour="bloquer une période, ouvrir une réservation" onClick={onAncien} testId="cal-ancien" />
              </div>
              {vue !== "jour" ? (
                <div className="ncal-r" style={{ marginTop: 8, marginBottom: 0 }}>
                  <span className="ncal-mois">
                    <button type="button" className="ncal-fl" aria-label="Mois précédent" onClick={() => setMois((m) => moisDecale(m, -1))}><Ic d={ICO.gauche} /></button>
                    <b>{nomMois.charAt(0).toUpperCase() + nomMois.slice(1)} {mois.slice(0, 4)}</b>
                    <button type="button" className="ncal-fl" aria-label="Mois suivant" onClick={() => setMois((m) => moisDecale(m, 1))}><Ic d={ICO.droite} /></button>
                  </span>
                </div>
              ) : null}
            </div>
          </>
        ) : (
        <div className="ncal-cmd">
          <div className="ncal-r">
            {vue !== "jour" ? (
              <span className="ncal-mois">
                <button type="button" className="ncal-fl" aria-label="Mois précédent" onClick={() => setMois((m) => moisDecale(m, -1))}><Ic d={ICO.gauche} /></button>
                <b>{nomMois.charAt(0).toUpperCase() + nomMois.slice(1)} {mois.slice(0, 4)}</b>
                <button type="button" className="ncal-fl" aria-label="Mois suivant" onClick={() => setMois((m) => moisDecale(m, 1))}><Ic d={ICO.droite} /></button>
              </span>
            ) : null}
            {vue !== "jour" ? <button type="button" className="nest-bt" onClick={() => setMois(today.slice(0, 7))}>Revenir à aujourd'hui</button> : null}
            <button type="button" className="nest-bt p" disabled title="Une écriture : elle n'est pas dans ce lot de lecture.">Bloquer une période</button>
            <LienAncien pour="bloquer une période, ouvrir une réservation" onClick={onAncien} testId="cal-ancien" />
            <span style={{ marginLeft: "auto" }} className="nest-seg">
              {[["tableau", "Tableau"], ["jour", "Jour"], ["liste", "Liste"]].map(([k, t]) => (
                <button key={k} type="button" className={vue === k ? "on" : ""} onClick={() => setVue(k)}>{t}</button>
              ))}
            </span>
          </div>
          <div className="ncal-r">
            <span className="nest-seg">
              {[["toutes", "Toutes plateformes"], ["airbnb", "Airbnb"], ["booking", "Booking"], ["direct", "Direct"]].map(([k, t]) => (
                <button key={k} type="button" className={f.plateforme === k ? "on" : ""} onClick={() => setF((x) => ({ ...x, plateforme: k }))}>
                  {k !== "toutes" ? <span className="ncal-pf"><i style={{ background: PLATEFORMES[k].point }} />{t}</span> : t}
                </button>
              ))}
            </span>
            <input className="nest-champ" placeholder="Voyageur ou logement…" value={f.texte} onChange={(e) => setF((x) => ({ ...x, texte: e.target.value }))} />
            <select className="nest-sel" value={f.logement} onChange={(e) => setF((x) => ({ ...x, logement: e.target.value }))} aria-label="Logement">
              <option value="">Tous les logements{logs ? ` (${logs.length})` : ""}</option>
              {(logs || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
            </select>
            <select className="nest-sel" value={f.statut} onChange={(e) => setF((x) => ({ ...x, statut: e.target.value }))} aria-label="Statut">
              <option value="tous">Tous les statuts</option>
              <option value="confirmees">Confirmées</option>
              <option value="bloque">Bloqué</option>
              <option value="annulees">Annulées</option>
            </select>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.gris }}>
            <input type="checkbox" checked={f.annulees} onChange={(e) => setF((x) => ({ ...x, annulees: e.target.checked }))} /> Annulées
          </label>
          <details style={{ fontSize: 11.5, color: C.gris, marginTop: 6 }}>
            <summary style={{ cursor: "pointer" }}>Couleurs et signes</summary>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingTop: 8 }}>
              {Object.entries(PLATEFORMES).map(([k, p]) => (
                <span key={k} className="ncal-pf"><i style={{ background: p.point }} />{p.nom}</span>
              ))}
              <span>Barré : annulée</span>
            </div>
          </details>
        </div>
        )}

        {vue === "jour" ? (
          <>
            <EtatLecture lecture={autourDuJour} quoi="Les séjours du jour" />
            <VueJour lecture={autourDuJour} jour={jour} setJour={setJour} today={today} heures={jour === today ? heuresDuJour : null} onAller={onAller} />
          </>
        ) : (
          <>
            <EtatLecture lecture={sejours} quoi="Les séjours du mois" onRelire={relireSejours} />
            {sejours.etat === "ok" && logs ? (
              vue === "tableau" ? (
                logsVisibles.length ? (
                  <Tableau logements={logsVisibles} sejours={visibles} jours={jours} today={today} exclus={exclus} g={etroit ? GRILLE.tel : GRILLE.ordi} />
                ) : (
                  <p className="nest-etat">Aucun logement à afficher.</p>
                )
              ) : (
                <Liste sejours={visibles} />
              )
            ) : null}
          </>
        )}
        {pied && vue !== "jour" ? (
          <p className="ncal-pied" data-testid="cal-pied">
            {pl(pied.nbResas, "réservation")} et {pl(pied.nbFermes, "blocage")} en {nomMois}
            {pied.nbAnnules ? ` · ${pl(pied.nbAnnules, "annulation non comptée", "annulations non comptées")}` : ""}
          </p>
        ) : null}
      </div>
    </div>
  );
}
